import {
  type DtoCreateLandAlreadyOwnedReq,
  type DtoCreateLandCurrentlyFinancedReq,
  type DtoCreateLandToBeLeasedReq,
  type DtoCreateLandToBePurchasedReq,
  type DtoGetLandResp,
  type DomainFrequencyType,
  type DomainLandSituationType,
  type DomainLeaseType,
} from "@porchpass/api-client";
import { createIsoDate, padNum, parseIsoDate, safeNumber } from "src/utils";

export type LandFormData = {
  id?: string;
  land_situation_type?: DomainLandSituationType;
  is_refinance?: boolean;
  use_as_down_payment?: boolean;
  down_payment_amount?: string;
  hoa_fee_amount?: string;
  hoa_fee_frequency?: DomainFrequencyType;
  land_owner?: string;
  land_value?: string;
  purchase_price_payoff?: string;
  purchase_month?: string;
  purchase_year?: string;
  acreage?: string;
  has_frontage_public_road?: boolean;
  has_paved_road?: boolean;
  lease_type?: DomainLeaseType;
  monthly_payment?: string;
  price_increase_explanation?: string;
};

export type LandAlreadyOwnedFormData = {
  use_as_down_payment?: boolean;
  hoa_fee_amount?: string;
  land_value?: string;
  purchase_month?: string;
  purchase_year?: string;
  acreage?: string;
  has_frontage_public_road?: boolean;
  has_paved_road?: boolean;
};

export type LandCurrentlyFinancedFormData = {
  is_refinance?: boolean;
  use_as_down_payment?: boolean;
  hoa_fee_amount?: string;
  land_owner?: string;
  land_value?: string;
  purchase_price_payoff?: string;
  purchase_month?: string;
  purchase_year?: string;
  acreage?: string;
  has_frontage_public_road?: boolean;
  has_paved_road?: boolean;
  monthly_payment?: string;
};

export type LandPurchasingFormData = {
  land_owner?: string;
  hoa_fee_amount?: string;
  acreage?: string;
  has_frontage_public_road?: boolean;
  has_paved_road?: boolean;
  purchase_price_payoff?: string;
};

export type LandLeasingFormData = {
  hoa_fee_amount?: string;
  land_owner?: string;
  lease_type?: DomainLeaseType;
  monthly_payment?: string;
  has_price_increase?: boolean;
  price_increase_explanation?: string;
};

export function fromLand(data: DtoGetLandResp): LandFormData {
  return {
    id: data?.id,
    land_situation_type: data?.land_situation_type,
    is_refinance: data?.is_refinance,
    use_as_down_payment: data?.use_as_down_payment,
    down_payment_amount: data?.down_payment_amount?.toString(),
    hoa_fee_amount: data?.hoa_fee_amount?.toString(),
    hoa_fee_frequency: data?.hoa_fee_frequency,
    land_owner: data?.land_owner,
    land_value: data?.land_value?.toString(),
    purchase_price_payoff: data?.purchase_price_payoff?.toString(),
    acreage: data?.acreage?.toString(),
    has_frontage_public_road: data?.has_frontage_public_road,
    has_paved_road: data?.has_paved_road,
    lease_type: data?.lease_type,
    monthly_payment: data?.monthly_payment?.toString(),
    price_increase_explanation: data?.price_increase_explanation,
    ...(data?.purchase_date
      ? (() => {
          const { month, year } = parseIsoDate(data.purchase_date);
          return {
            purchase_month: padNum(month).toString(),
            purchase_year: padNum(year).toString(),
          };
        })()
      : {}),
  };
}

export function toCreateLandAlreadyOwnedReq(
  data: LandAlreadyOwnedFormData,
): DtoCreateLandAlreadyOwnedReq {
  return {
    use_as_down_payment: !!data?.use_as_down_payment,
    hoa_fee_amount: safeNumber(data?.hoa_fee_amount),
    ...(data?.use_as_down_payment
      ? {
          land_value: safeNumber(data.land_value ?? undefined),
          purchase_date: createIsoDate(
            safeNumber(data?.purchase_year),
            safeNumber(data?.purchase_month),
          ),
          acreage: safeNumber(data.acreage),
          has_frontage_public_road: data.has_frontage_public_road,
          has_paved_road: data.has_paved_road,
        }
      : {}),
  };
}

export function toCreateLandCurrentlyFinancedReq(
  data: LandCurrentlyFinancedFormData,
): DtoCreateLandCurrentlyFinancedReq {
  return {
    is_refinance: data?.is_refinance ?? false,
    use_as_down_payment: data?.use_as_down_payment ?? false,
    hoa_fee_amount: safeNumber(data?.hoa_fee_amount),
    monthly_payment: safeNumber(data?.monthly_payment),
    ...(data?.use_as_down_payment || data?.is_refinance
      ? {
          purchase_price_payoff: safeNumber(data.purchase_price_payoff),
          land_owner: data.land_owner,
          land_value: safeNumber(data.land_value),
          purchase_date: createIsoDate(
            safeNumber(data.purchase_year),
            safeNumber(data.purchase_month),
          ),
          acreage: safeNumber(data.acreage),
          has_frontage_public_road: data?.has_frontage_public_road,
          has_paved_road: data?.has_paved_road,
        }
      : {}),
  };
}

export function toCreateLandPurchasingReq(
  data: LandPurchasingFormData,
): DtoCreateLandToBePurchasedReq {
  return {
    land_owner: data?.land_owner,
    hoa_fee_amount: safeNumber(data?.hoa_fee_amount),
    purchase_price_payoff: safeNumber(data?.purchase_price_payoff),
    acreage: safeNumber(data?.acreage),
    has_frontage_public_road: data?.has_frontage_public_road,
    has_paved_road: data?.has_paved_road,
  };
}

export function toCreateLandLeasingReq(
  data: LandLeasingFormData,
): DtoCreateLandToBeLeasedReq {
  return {
    hoa_fee_amount: safeNumber(data?.hoa_fee_amount),
    land_owner: data?.land_owner,
    lease_type: data?.lease_type,
    monthly_payment: safeNumber(data?.monthly_payment),
    // The DLA endpoint requires this field and reads an empty string as
    // "no price increase", so "No" must send "" rather than omit the field.
    price_increase_explanation:
      data?.has_price_increase === false
        ? ""
        : data?.price_increase_explanation,
  };
}
